<template>
    <div :class="{ 'card': !noWrapper, 'data-list-card': true }">
        <b-card-header v-if="showHeader" :class="{'show-header-as-footer': showHeaderAsFooter}">
            <div class="combo-flex">
                <b-pagination
                    v-model="currentPage"
                    :total-rows="totalRows"
                    :per-page="perPage"
                    first-number
                    last-number
                    limit="8"
                ></b-pagination>
                <div class="combo-flex__right">
                    <span class="combo-text">Visar {{ meta.from || 0 }} till {{ meta.to || meta.total }} av {{ totalRows }}</span>
                    <v-select :clearable="false"
                              :options="perPageOptions"
                              :value="perPage"
                              @input="perPage = $event">
                    </v-select>
                    <div class="combo-flex__tools" v-if="fields.length">
                        <b-button-group>
                            <b-button v-if="$slots['data-tools']" variant="outline-primary" id="data-tools">
                                <BIconTools/>
                            </b-button>
                            <b-button v-b-tooltip="'Kolumnhantering'" variant="outline-primary" id="column-tool" @click="showModal = !showModal">
                                <BIconLayoutThreeColumns/>
                            </b-button>
                        </b-button-group>
                        <b-popover ref="data-tool" v-if="$slots['data-tools']" target="data-tools"
                                   triggers="hover focus">
                            <template #title>
                                <BIconTools/>
                                Verktyg
                            </template>
                            <slot name="data-tools"></slot>
                        </b-popover>
                        <b-modal v-model="showModal" size="xl">
                            <h2>
                                <BIconLayoutThreeColumns/>
                                Kolumnhantering
                            </h2>
                            <b-alert show>
                                Klicka eller drag och släp kolumner mellan panelerna.
                            </b-alert>
                            <b-card>
                                <div style="float:right;">
                                    <b-button variant="outline-primary" @click="resetColumns">Återställ kolumner</b-button>
                                    <b-button variant="primary" @click="saveCurrentVisible">Spara synliga kolumner</b-button>
                                </div>
                            </b-card>
                            <div class="column-grid">
                                <b-card no-body>
                                    <b-card-header>
                                        <b>Synliga</b>
                                    </b-card-header>
                                    <b-card-body>
                                        <draggable class="draggable" :list="visibleKeys" group="columns">
                                            <div v-for="field in visibleKeys" :key="field.name">
                                                <span v-if="field.label.length" class="column-action"
                                                      @click="hideField(field)">{{ field.label }}</span>
                                            </div>
                                        </draggable>
                                    </b-card-body>
                                </b-card>
                                <b-card no-body>
                                    <b-card-header>
                                        <b>Dolda</b>
                                    </b-card-header>
                                    <b-card-body>
                                        <draggable class="draggable" :list="hiddenKeys" group="columns">
                                            <div v-for="field in hiddenKeys" :key="field.name">
                                                <span v-if="field.label.length" class="column-action"
                                                      @click="addField(field)">{{ field.label }}</span>
                                            </div>
                                        </draggable>
                                    </b-card-body>
                                </b-card>
                            </div>
                        </b-modal>
                    </div>
                </div>
            </div>
        </b-card-header>
        <b-table
            v-bind="$attrs"
            v-on="$listeners"
            :items="internalDataProvider"
            :per-page="perPage"
            :current-page="currentPage"
            ref="table"
            :fields="visibleFields"
            responsive
            :class="{ 'has-row-clicked': !!($listeners['row-clicked']), 'data-list-content-area': true }"
        >
            <template v-for="(_, slot) of $scopedSlots" v-slot:[slot]="scope">
                <slot :name="slot" v-bind="scope"/>
            </template>
            <template #cell()="data">
                <b-badge v-if="data.value.dataType === 'badge'" :variant="data.value.variant">
                    {{ data.value.text }}
                </b-badge>
                <span v-else class="data-value">{{ data.value }}</span>
            </template>
        </b-table>
        <floating-center-spinner :loading="loading">
            {{ loadingText }}
        </floating-center-spinner>
        <b-card-footer v-if="showFooter">
            <div class="combo-flex">
                <section v-b-tooltip.hover title="Byt sida med shift+vänster/högerpil">
                    <b-pagination
                        v-model="currentPage"
                        :total-rows="totalRows"
                        :per-page="perPage"
                        first-number
                        last-number
                        limit="8"
                    ></b-pagination>
                </section>
                <div class="combo-flex__right">
                    <span class="combo-text">Visar {{ meta.from || 0 }} till {{ meta.to || meta.total }} av {{ totalRows }}</span>
                    <v-select :clearable="false" :options="perPageOptions" :value="perPage"
                              @input="perPage = $event"></v-select>
                </div>
            </div>
        </b-card-footer>
    </div>
</template>
<script>
import vSelect from 'vue-select';
import ProductRepository from '@services/repositories/Product/ProductRepository';
import draggable from 'vuedraggable';
import FloatingCenterSpinner from '@components/FloatingCenterSpinner.vue';
import { BIconTools, BIconLayoutThreeColumns } from 'bootstrap-vue';

export default {
    props: {
        fields: {
            type: Array,
            default() {
                return [];
            },
        },
        additionalFields: {
            type: Array,
            default() {
                return [];
            },
        },
        dataProvider: {
            type: Function,
            default: async (ctx) => {
                const repo = new ProductRepository();
                // eslint-disable-next-line no-return-await
                return await repo.buildFromBootstrapTableContext(ctx);
            },
        },
        state: {
            type: Object,
            default: null,
        },
        noWrapper: {
            type: Boolean,
            default: false,
        },
        showHeader: {
            type: Boolean,
            default: true,
        },
        showHeaderAsFooter: {
            type: Boolean,
            default: false,
        },
        showFooter: {
            type: Boolean,
            default: true,
        },
        loadingText: {
            type: String,
            default: 'Hämtar data...',
        },
        useStaticHeight: {
            type: Boolean,
            default: false,
        },
    },
    components: {
        FloatingCenterSpinner,
        vSelect,
        draggable,
        BIconTools,
        BIconLayoutThreeColumns,
    },
    watch: {
        additionalFields() {
            if (!this.hasSavedColumns) {
                this.buildColumns();
                this.sortHiddenFields();
                Object.assign(this.$data, this.state);
            }

            if (this.hasSavedColumns) {
                this.processSavedColumns();
            }
        },
    },
    mounted() {
        this.hiddenKeys = [...this.additionalFields];
        this.visibleKeys = [...this.fields];
        this.processSavedColumns();
        this.sortHiddenFields();
        Object.assign(this.$data, this.state);

        if (this.useStaticHeight) {
            const { top } = this.$el.getBoundingClientRect();
            this.$el.style.height = 'calc(100vh - 16px - ' + top.toFixed(0) + 'px)';
        }
    },
    created() {
        window.addEventListener('keydown', this.keyBindingListener);
    },
    destroyed() {
        window.removeEventListener('keydown', this.keyBindingListener);
    },
    data() {
        return {
            loading: false,
            hasSavedColumns: false,
            currentCount: 0,
            totalRows: 0,
            perPage: 50,
            currentPage: 1,
            perPageOptions: [25, 50, 100, 250, 500, 1000, 2500],
            hiddenKeys: [],
            visibleKeys: [],
            showModal: false,
            meta: {
                to: 0,
                from: 0,
            },
        };
    },
    computed: {
        visibleFields() {
            return [...this.visibleKeys];
        },
    },
    methods: {
        getFields() {
            return [...this.fields, ...this.additionalFields];
        },
        sortHiddenFields() {
            this.hiddenKeys = this.hiddenKeys.sort((a, b) => {
                if (a.label > b.label) {
                    return 1;
                }

                return (b.label > a.label) ? -1 : 0;
            });
        },
        hideField(field) {
            let fieldKey = field;
            if (typeof field === 'object') {
                fieldKey = field.key;
            }
            const popped = this.visibleKeys.filter((item) => item.key === fieldKey);
            this.visibleKeys = this.visibleKeys.filter((item) => item.key !== fieldKey);
            this.hiddenKeys.push(popped[0]);
            this.sortHiddenFields();
        },
        addField(field) {
            let fieldKey = field;
            if (typeof field === 'object') {
                fieldKey = field.key;
            }
            const popped = this.hiddenKeys.filter((item) => item.key === fieldKey);
            this.hiddenKeys = this.hiddenKeys.filter((item) => item.key !== fieldKey);
            this.visibleKeys.push(popped[0]);
            this.sortHiddenFields();
        },
        async internalDataProvider(ctx) {
            this.loading = true;
            const results = await this.dataProvider(ctx);

            if (results.meta) {
                this.meta = results.meta;
                this.totalRows = results.meta.total;
                this.currentPage = results.meta.current_page;
            }

            results.context = ctx;
            this.$emit('update', results);
            this.scrollToTop();

            this.loading = false;
            return results.data;
        },
        scrollToTop() {
            this.$refs.table?.$el?.scrollTo(0, 0);
            document.querySelector('.content-wrapper').scrollTo(0, 0);
        },
        onError() {
            this.display = false;
        },
        keyBindingListener(event) {
            if (event.shiftKey && event.key === 'ArrowRight') {
                this.currentPage += 1;
            }

            if (event.shiftKey && event.key === 'ArrowLeft') {
                if (this.currentPage >= 2) {
                    this.currentPage -= 1;
                }
            }
        },
        buildColumns() {
            this.hiddenKeys = [...this.additionalFields];
            this.visibleKeys = [...this.fields];
        },
        processSavedColumns() {
            const currentRoute = this.$route.name;
            const savedColumns = JSON.parse(localStorage.getItem('data-list-columns')) || {};

            if (typeof savedColumns === 'object' && savedColumns[currentRoute]) {
                this.visibleKeys.forEach((item) => this.hideField(item.key));
                const fields = savedColumns[currentRoute];

                if (Array.isArray(fields)) {
                    this.hasSavedColumns = true;

                    fields.forEach((item) => {
                        if (fields) {
                            this.addField(item);
                        }
                    });
                }
            }
        },
        saveCurrentVisible() {
            const currentRoute = this.$route.name;
            const savedColumns = JSON.parse(localStorage.getItem('data-list-columns')) || {};
            savedColumns[currentRoute] = this.visibleKeys.map((item) => item.key);
            localStorage.setItem('data-list-columns', JSON.stringify(savedColumns));
            this.$bvToast.toast('Kolumner sparade!', { variant: 'success' });
        },
        resetColumns() {
            const currentRoute = this.$route.name;
            const savedColumns = JSON.parse(localStorage.getItem('data-list-columns')) || {};
            delete savedColumns[currentRoute];
            localStorage.setItem('data-list-columns', JSON.stringify(savedColumns));
            this.buildColumns();
            this.$bvToast.toast('Kolumner återställd!', { variant: 'success' });
        },
    },
};
</script>
<style lang="scss">
.has-row-clicked .table tbody tr:hover {
    cursor: pointer;
}

.column-action {
    display: block;
    cursor: pointer;
    padding: 4px 6px;
    background-color: #fafafa;
    margin: 2px;

    &:hover {
        text-decoration: underline;
    }

    &::before {
        font-family: 'icomoon' !important;
        speak: none;
        font-style: normal;
        font-weight: normal;
        font-variant: normal;
        text-transform: none;
        line-height: 1;
        min-width: 1em;
        display: inline-block;
        text-align: center;
        font-size: 16px;
        vertical-align: middle;
        position: relative;
        top: -1px;
        -webkit-font-smoothing: antialiased;
        -moz-osx-font-smoothing: grayscale;
        content: '\e9c4 \e9c7';
        transform: rotate(90deg);
    }
}

.combo-flex {
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.combo-flex__right {
    display: grid;
    grid-template-columns: max-content 100px min-content;
    align-items: center;
    gap: 1rem;
}

.combo-flex__tools {
    display: grid;
    grid-template-columns: 1fr 1fr;
    align-items: center;
    gap: 1rem;
}

.column-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px;
}

.data-list-card {
    display: flex;
    flex-direction: column;
    scrollbar-width: thin;
    scrollbar-color: black white;

    & > .card-header:not(.show-header-as-footer) {
        order: 1;
    }

    .card-header,
    .card-footer {
        padding: 8px;
    }

    .data-list-content-area {
        order: 2;
    }

    .table-responsive.data-list-content-area {
       margin-bottom: 0;
    }

    .card-footer,
    .show-header-as-footer {
        order: 3;
        .vs__dropdown-menu {
            translate: 0 -121%;
        }
    }
}

.content-box {
    position: relative;
}
</style>
